import { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { useSettings } from "./SettingsContext";
import { getPendingAuthSession } from "../lib/authSession";

const ShopModeContext = createContext(null);

function getCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

function setCookie(name, value, days = 365) {
  if (typeof document === "undefined") return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

export function ShopModeProvider({ children }) {
  const { user, loading } = useAuth();
  const { settings } = useSettings();
  const siteMode = settings?.site_mode || "both"; // "both" | "b2b" | "b2c"
  const storeMode = settings?.store_mode || "HYBRID"; // "HYBRID" | "RETAIL_ONLY" | "B2B_ONLY" | "NONE"

  const isWholesaleOpen =
    settings?.wholesale_enabled !== false &&
    (siteMode === "both" || siteMode === "b2b" || storeMode === "HYBRID" || storeMode === "B2B_ONLY");
  const isRetailOpen =
    settings?.retail_enabled !== false &&
    (siteMode === "both" || siteMode === "b2c" || storeMode === "HYBRID" || storeMode === "RETAIL_ONLY");
  const isHybrid =
    (storeMode === "HYBRID" || siteMode === "both") && isRetailOpen && isWholesaleOpen;
  const bothStoresOpen = isHybrid;

  const [mode, setModeState] = useState(() => {
    return localStorage.getItem("zt_shop_mode") || getCookie("zt_shop_mode") || "retail";
  });
  const [showChoiceModal, setShowChoiceModal] = useState(false);

  // Sync mode with site_mode / store_mode and ask user for choice when in HYBRID mode
  useEffect(() => {
    if (siteMode === "b2b" || storeMode === "B2B_ONLY" || (!isRetailOpen && isWholesaleOpen)) {
      setModeState("wholesale");
      setShowChoiceModal(false);
    } else if (siteMode === "b2c" || storeMode === "RETAIL_ONLY" || (!isWholesaleOpen && isRetailOpen)) {
      setModeState("retail");
      setShowChoiceModal(false);
    } else if (isHybrid) {
      if (!loading) {
        const pendingAuth = getPendingAuthSession();
        const choicePromptedInSession = sessionStorage.getItem("zt_store_choice_prompted");
        if (!choicePromptedInSession && !pendingAuth) {
          setShowChoiceModal(true);
        }
      }
    } else {
      setShowChoiceModal(false);
    }
  }, [loading, siteMode, storeMode, isRetailOpen, isWholesaleOpen, isHybrid]);

  const setMode = (newMode) => {
    const targetMode =
      siteMode === "b2b" || storeMode === "B2B_ONLY"
        ? "wholesale"
        : siteMode === "b2c" || storeMode === "RETAIL_ONLY"
        ? "retail"
        : newMode;
    setModeState(targetMode);
    localStorage.setItem("zt_shop_mode", targetMode);
    setCookie("zt_shop_mode", targetMode);
  };

  const selectModeAndCloseModal = (chosenMode) => {
    setMode(chosenMode);
    sessionStorage.setItem("zt_store_choice_prompted", "true");
    setShowChoiceModal(false);
  };

  const toggleMode = () => {
    const target = effectiveMode === "wholesale" ? "retail" : "wholesale";
    setMode(target);
  };

  const openChoiceModal = () => {
    if (isHybrid) {
      setShowChoiceModal(true);
    }
  };

  const closeChoiceModal = () => {
    sessionStorage.setItem("zt_store_choice_prompted", "true");
    setShowChoiceModal(false);
  };

  const effectiveMode =
    siteMode === "b2b" || storeMode === "B2B_ONLY"
      ? "wholesale"
      : siteMode === "b2c" || storeMode === "RETAIL_ONLY"
      ? "retail"
      : mode;

  return (
    <ShopModeContext.Provider
      value={{
        mode: effectiveMode,
        isRetail: effectiveMode === "retail",
        isWholesale: effectiveMode === "wholesale",
        siteMode,
        storeMode,
        isHybrid,
        bothStoresOpen,
        setMode,
        toggleMode,
        showChoiceModal: isHybrid && showChoiceModal,
        selectModeAndCloseModal,
        openChoiceModal,
        closeChoiceModal,
      }}
    >
      {children}
    </ShopModeContext.Provider>
  );
}

export const useShopMode = () => {
  const ctx = useContext(ShopModeContext);
  if (!ctx) {
    return {
      mode: "retail",
      isRetail: true,
      isWholesale: false,
      setMode: () => {},
      showChoiceModal: false,
      selectModeAndCloseModal: () => {},
      openChoiceModal: () => {},
      closeChoiceModal: () => {},
    };
  }
  return ctx;
};
